/**
 * Workspace, account and identity presentation (prompt.md §6.2, §8.3, §45, §71).
 *
 * WHY the plane is always visible: a person may hold a personal identity and one
 * or more workplace identities, and their data is isolated between them. A user
 * must never have to guess which one they are looking at, and switching must be
 * an explicit act that clears the previous context rather than a silent toggle.
 */

import { Badge, EmptyState } from "../primitives/Feedback.tsx";
import { Button } from "../primitives/Button.tsx";
import { Card } from "../primitives/Surfaces.tsx";
import { Menu } from "../primitives/Surfaces.tsx";
import { MUTED_TEXT, rowStyle, stackStyle } from "../styles.ts";
import { formatBytes, formatRelativeTime } from "../logic/format.ts";

export interface WorkspaceSummary {
  readonly workspaceId: string;
  readonly name: string;
  readonly kind: "personal" | "team";
  readonly plan: string;
  readonly planStatus?: "effective" | "cached";
  readonly role: string;
}

export interface WorkspaceSwitcherProps {
  readonly workspaces: readonly WorkspaceSummary[];
  readonly activeWorkspaceId: string | null;
  readonly onSelect: (workspaceId: string) => void | Promise<void>;
  readonly onManage?: () => void;
}

/**
 * Workspace switcher. Personal and workplace workspaces are grouped and labelled
 * by plane so a work workspace is never mistaken for a personal one.
 */
export function WorkspaceSwitcher({ workspaces, activeWorkspaceId, onSelect, onManage }: WorkspaceSwitcherProps) {
  const active = workspaces.find((workspace) => workspace.workspaceId === activeWorkspaceId) ?? null;
  const items = workspaces.map((workspace) => ({
    id: workspace.workspaceId,
    label: `${workspace.kind === "team" ? "Team" : "Personal"} · ${workspace.name} — ${workspace.plan} plan, ${workspace.role}${workspace.planStatus === "cached" ? "; billing status unavailable" : ""}`,
    onSelect: () => void onSelect(workspace.workspaceId),
  }));
  if (onManage) items.push({ id: "__manage", label: "Manage workspaces", onSelect: () => { onManage(); } });

  return (
    <div style={rowStyle("var(--dayvise-space-xs)", "flex-start")}>
      <Menu label="Switch workspace" trigger={active ? active.name : "Select a workspace"} items={items} />
      {active ? <PlaneBadge kind={active.kind} /> : null}
      {active?.planStatus === "cached" ? (
        <span role="status" style={{ color: "var(--dayvise-color-warning)", fontSize: "var(--dayvise-font-size-xs)" }}>
          Plan may be out of date: billing status could not be checked.
        </span>
      ) : null}
    </div>
  );
}

/**
 * The personal/work marker. It is a badge rather than a subtle colour because
 * confusing the two planes is the exact mistake the identity model exists to
 * prevent.
 */
export function PlaneBadge({ kind }: { readonly kind: "personal" | "team" }) {
  return (
    <Badge tone={kind === "team" ? "accent" : "neutral"}>{kind === "team" ? "Workplace" : "Personal"}</Badge>
  );
}

export interface AccountSwitcherProps {
  readonly accounts: readonly {
    readonly identityId: string;
    readonly label: string;
    readonly plane: "personal" | "work";
    readonly email: string | null;
  }[];
  readonly activeIdentityId: string | null;
  readonly onSwitch: (identityId: string) => void | Promise<void>;
  readonly onSignOut: () => void | Promise<void>;
}

/**
 * Account switcher. Switching planes is destructive to the client's cached
 * context by design (prompt.md §71) — the caller must clear conversations,
 * memories, files and credentials before showing the new context, and the UI
 * says so.
 */
export function AccountSwitcher({ accounts, activeIdentityId, onSwitch, onSignOut }: AccountSwitcherProps) {
  const active = accounts.find((account) => account.identityId === activeIdentityId) ?? null;
  return (
    <div style={stackStyle("var(--dayvise-space-xs)")}>
      <Menu
        label="Switch account"
        trigger={active ? active.label : "Choose an account"}
        items={accounts.map((account) => ({
          id: account.identityId,
          label: `${account.plane === "work" ? "Work" : "Personal"} · ${account.label}${account.email ? ` (${account.email})` : ""}`,
          onSelect: () => void onSwitch(account.identityId),
        }))}
      />
      <span style={{ ...MUTED_TEXT, fontSize: "var(--dayvise-font-size-xs)" }}>
        Switching accounts clears everything cached for the previous account. Personal and workplace data are never mixed.
      </span>
      <Button size="sm" variant="ghost" onClick={() => void onSignOut()}>
        Sign out
      </Button>
    </div>
  );
}

export interface FileArtifact {
  readonly fileId: string;
  readonly path: string;
  readonly version: number;
  readonly mimeType: string;
  readonly size: number;
  readonly storageProvider: "structured" | "blob";
  readonly updatedAt: string;
}

export interface FileArtifactListProps {
  readonly files: readonly FileArtifact[];
  readonly onOpen?: (file: FileArtifact) => void | Promise<void>;
  readonly openLabel?: string;
  readonly emptyLabel?: string;
}

/**
 * Generated files. The storage provider is shown because it is a user choice —
 * a workspace on BYO object storage must be able to see that its artifacts are
 * going where it configured them (prompt.md §10, §55).
 */
export function FileArtifactList({ files, onOpen, openLabel = "Open", emptyLabel = "No files yet" }: FileArtifactListProps) {
  if (files.length === 0) {
    return <EmptyState title={emptyLabel} description="Files an agent writes during a run appear here." />;
  }
  return (
    <div style={stackStyle("var(--dayvise-space-sm)")}>
      {files.map((file) => (
        <Card
          key={file.fileId}
          title={file.path}
          subtitle={`${file.mimeType} · ${formatBytes(file.size)} · v${file.version} · ${formatRelativeTime(file.updatedAt)}`}
          footer={
            onOpen ? (
              <Button size="sm" variant="secondary" onClick={() => void onOpen(file)}>
                {openLabel}
              </Button>
            ) : undefined
          }
        >
          <Badge tone={file.storageProvider === "blob" ? "accent" : "neutral"}>
            {file.storageProvider === "blob" ? "Object storage" : "Structured store"}
          </Badge>
        </Card>
      ))}
    </div>
  );
}

export interface StorageRouteSummary {
  readonly structured: "d1_shared" | "postgres_byo";
  readonly blob: "r2_managed" | "s3_byo";
  readonly health: "healthy" | "degraded" | "unavailable";
  readonly lastCheckedAt: string | null;
}

/**
 * Where this workspace's data actually lives. A BYO workspace must be able to
 * confirm that content is not sitting in the managed store (prompt.md §79).
 */
export function StorageRouteCard({ route, onChange }: { readonly route: StorageRouteSummary; readonly onChange?: () => void }) {
  return (
    <Card
      title="Data location"
      subtitle={route.lastCheckedAt ? `Checked ${formatRelativeTime(route.lastCheckedAt)}` : "Not checked yet"}
      footer={onChange ? <Button size="sm" variant="secondary" onClick={onChange}>Change storage</Button> : undefined}
    >
      <div style={stackStyle("var(--dayvise-space-xs)")}>
        <div style={rowStyle("var(--dayvise-space-xs)", "flex-start")}>
          <Badge tone={route.health === "healthy" ? "success" : route.health === "degraded" ? "warning" : "danger"}>{route.health}</Badge>
        </div>
        <span style={{ fontSize: "var(--dayvise-font-size-sm)" }}>
          Structured data: {route.structured === "postgres_byo" ? "your PostgreSQL database" : "Dayvise-managed D1"}
        </span>
        <span style={{ fontSize: "var(--dayvise-font-size-sm)" }}>
          Files and objects: {route.blob === "s3_byo" ? "your S3-compatible storage" : "Dayvise-managed R2"}
        </span>
        {route.structured === "postgres_byo" || route.blob === "s3_byo" ? (
          <span style={{ ...MUTED_TEXT, fontSize: "var(--dayvise-font-size-xs)" }}>
            Control-plane metadata (account, plan, routing, usage, audit) stays with Dayvise; conversation content and files do not.
          </span>
        ) : null}
      </div>
    </Card>
  );
}
